<!-- ══════════════════════════════════════════════════════
     ASSESSMENT LANDING — assessment-landing.component.html
     Prefix: al-   |  No orange   |  Sehyog sh- token colours
     CONTENT UPDATED: Years 3–11 positioning, thinking-skills focus
══════════════════════════════════════════════════════ -->

<!-- ══ HERO — 3-column desktop: [Image | Copy | Form] ══ -->
<section class="al-hero">
  <div class="al-hero-inner">

    <!-- COL 1: Full-height image -->
    <div class="al-hero-img-col">
      <div class="al-hero-img-wrap">
        <img class="al-hero-img"
             src="https://images.pexels.com/photos/8423093/pexels-photo-8423093.jpeg"
             alt="Future-Ready Assessments for Years 3 to 11"
             onerror="this.style.display='none'" />
      </div>
    </div>

    <!-- COL 2: Headline + copy -->
    <div class="al-hero-copy">

      <h1 class="al-h1">
        Unlock Your Child's<br/>
        <span class="al-h1-hi">Full Potential</span><br/>
        Years 3 to 11
      </h1>

      <p class="al-hero-sub">
        Every child has potential. We help them discover it — a structured
        assessment that reveals not just what your child knows, but how
        they think, learn, and solve problems.
      </p>

      <!-- powered-by bar sits nicely under the copy on desktop -->
      <div class="al-rar-inline">
        <span>Includes a full
          <strong>Digital Performance Report &amp; 1-to-1 Parent Consultation</strong>
        </span>
      </div>
    </div>

    <!-- COL 3: Registration form -->
    <div id="al-register" class="al-form-wrap">
      <div class="al-form-card">

        <div class="al-form-success" *ngIf="formSubmitted">
          <div class="al-success-icon">✓</div>
          <h3>You're Registered!</h3>
          <p>Check your inbox for confirmation details and your consultation invite.</p>
        </div>

        <ng-container *ngIf="!formSubmitted">
          <h3 class="al-form-title">Book a Thinking Challenge Assessment</h3>
          <p class="al-form-sub">Free to enter. Expert-marked. Takes 90 seconds to register.</p>

          <div class="al-field">
            <input class="al-input" type="text" placeholder="Parent's Full Name" [(ngModel)]="form.parentName" />
          </div>
          <div class="al-field">
            <input class="al-input" type="email" placeholder="Email Address" [(ngModel)]="form.email" />
          </div>
          <div class="al-field">
            <input class="al-input" type="tel" placeholder="Phone Number (incl. country code)" [(ngModel)]="form.phone" />
          </div>
          <div class="al-field">
            <select class="al-select" [(ngModel)]="form.yearGroup">
              <option value="">Child's Current School Year</option>
              <option value="Year 3">Year 3</option>
              <option value="Year 4">Year 4</option>
              <option value="Year 5">Year 5</option>
              <option value="Year 6">Year 6</option>
              <option value="Year 7">Year 7</option>
              <option value="Year 8">Year 8</option>
              <option value="Year 9">Year 9</option>
              <option value="Year 10">Year 10</option>
              <option value="Year 11">Year 11</option>
            </select>
          </div>

          <div class="al-form-error" *ngIf="formError">
            Please fill in all fields before submitting.
          </div>

          <button class="al-btn-submit" (click)="onSubmit()">
            Register for a Thinking Challenge →
          </button>

          <div class="al-form-trust">
            <span class="al-trust-item">🔒 Secure</span>
            <span class="al-trust-item">✅ 100% Free</span>
            <span class="al-trust-item">📧 Instant Confirmation</span>
          </div>
        </ng-container>

      </div>
    </div>
  </div>
</section>

<!-- ══ TRUST STRIP ══ -->
<div class="al-trust-strip">
  <div class="al-trust-inner">
    <div class="al-trust-stat">
      <div class="al-trust-icon">👨‍👩‍👧</div>
      <div class="al-trust-text"><strong>4,800+</strong><span>Families registered</span></div>
    </div>
    <div class="al-trust-stat">
      <div class="al-trust-icon">🧠</div>
      <div class="al-trust-text"><strong>93%</strong><span>Report improved confidence after their consultation</span></div>
    </div>
    <div class="al-trust-stat">
      <div class="al-trust-icon">📊</div>
      <div class="al-trust-text"><strong>Years 3–11</strong><span>Every year group, one clear pathway</span></div>
    </div>
    <div class="al-trust-stat">
      <div class="al-trust-icon">⭐</div>
      <div class="al-trust-text"><strong>4.9 / 5</strong><span>Parent satisfaction score</span></div>
    </div>
  </div>
</div>

<!-- ══ SECTION 2: DIAGNOSTIC PILLARS ══ -->
<section class="al-section al-pillars-section">
  <div class="al-section-inner">
    <div class="al-section-head al-centered">
      <div class="al-label">Beyond Traditional Testing</div>
      <h2 class="al-heading">We Don't Just Measure<br/>What They Know. We Measure How They Think.</h2>
      <p class="al-sub">Most assessments tell you a score. Sehyog's assessments tell you exactly how your child reasons, solves problems, and applies knowledge in unfamiliar situations.</p>
    </div>
    <div class="al-rar-bar">
      <span class="al-rar-dot"></span>
      <span class="al-rar-text">
        Every family receives a
        <strong>Comprehensive Digital Performance Report &amp; Parent Consultation</strong>
      </span>
    </div>
    <div class="al-pillars-grid">
      <div class="al-pillar-card" [ngClass]="pillar.cls" *ngFor="let pillar of pillars">
        <div class="al-pillar-num">{{pillar.num}}</div>
        <h3 class="al-pillar-title">{{pillar.title}}</h3>
        <p class="al-pillar-body">{{pillar.body}}</p>
        <span class="al-pillar-tag">{{pillar.tag}}</span>
      </div>
    </div>
  </div>
</section>

<!-- ══ ASSESSMENT STRUCTURE ══ -->
<section class="al-section al-structure-section">
  <div class="al-section-inner">
    <div class="al-section-head al-centered">
      <div class="al-label al-label-gold">Assessment Structure</div>
      <h2 class="al-heading al-heading-white">One Challenge. Two Subjects. Full Picture.</h2>
      <p class="al-sub al-sub-faded">A single, age-appropriate Mathematics &amp; English Thinking Challenge, curriculum-aligned and mapped to your child's exact year group.</p>
    </div>
    <div class="al-structure-grid">
      <div class="al-structure-card" [ngClass]="sc.cls" *ngFor="let sc of structureCards">
        <div class="al-sc-num">{{sc.num}}</div>
        <h4 class="al-sc-title">{{sc.title}}</h4>
        <div class="al-sc-mark">{{sc.mark}}</div>
        <ul class="al-sc-topics">
          <li *ngFor="let t of sc.topics">{{t}}</li>
        </ul>
      </div>
    </div>
  </div>
</section>

<!-- ══ HOW IT WORKS ══ -->
<section class="al-section al-hiw-section" id="al-how-it-works">
  <div class="al-section-inner">
    <div class="al-section-head al-centered">
      <div class="al-label">How It Works</div>
      <h2 class="al-hiw-heading">
        Your Child's Learning Journey<br/>
        <span class="al-hiw-hi">in Six Simple Steps</span>
      </h2>
      <p class="al-sub al-sub-center">We've made the process frictionless — so you can focus on your child's progress, not the admin.</p>
    </div>
    <div class="al-hiw-grid">
      <div class="al-steps">
        <div class="al-step" *ngFor="let step of howSteps">
          <div class="al-step-num" [ngClass]="step.cls">{{step.num}}</div>
          <div class="al-step-content">
            <h5 class="al-step-title">{{step.title}}</h5>
            <p class="al-step-body">{{step.body}}</p>
          </div>
        </div>
      </div>
      <div class="al-webinar-card">
        <div class="al-webinar-free-badge">🎓 Free Consultation</div>
        <h3 class="al-webinar-title">1-to-1 Parent Consultation</h3>
        <div class="al-webinar-date">📅 Included free with every registration</div>
        <p class="al-webinar-desc">
          A dedicated session with an experienced Sehyog educator to walk through your child's
          report, thinking skills profile, and exactly what the data means for their next
          academic year.
        </p>
        <ul class="al-webinar-list">
          <li *ngFor="let item of consultationChecklist">{{item}}</li>
        </ul>
        <button class="al-webinar-btn" (click)="scrollToRegister()">Register Now →</button>
        <p class="al-webinar-note">*Cohort Places Are Strictly Limited</p>
      </div>
    </div>
  </div>
</section>

<!-- ══ WHAT YOUR CHILD WILL RECEIVE ══ -->
<section class="al-section al-receives-section">
  <div class="al-section-inner">
    <div class="al-section-head al-centered">
      <div class="al-label">What Your Family Will Receive</div>
      <h2 class="al-heading">More Than Just a Mark</h2>
      <p class="al-sub">Every registered family receives a rich results package within 48 hours — built on curriculum-aligned, evidence-based analysis, not generic automated scoring.</p>
    </div>
    <div class="al-receives-grid">
      <div class="al-receives-card" *ngFor="let rc of receivesCards">
        <div class="al-rc-icon">{{rc.icon}}</div>
        <div class="al-rc-text">
          <h5 class="al-rc-title">{{rc.title}}</h5>
          <p class="al-rc-body">{{rc.body}}</p>
        </div>
      </div>
    </div>
  </div>
</section>

<!-- ══ FAQ ══ -->
<section class="al-section al-faq-section">
  <div class="al-section-inner">
    <div class="al-section-head al-centered">
      <div class="al-label">FAQs</div>
      <h2 class="al-heading">Your Questions, Answered</h2>
    </div>
    <div class="al-faq-list">
      <div class="al-faq-item" [ngClass]="{'al-faq-open': faq.open}" *ngFor="let faq of faqs; let i = index">
        <div class="al-faq-q" (click)="toggleFaq(i)">
          <span>{{faq.q}}</span>
          <span class="al-faq-arrow">▾</span>
        </div>
        <div class="al-faq-a" [style.maxHeight]="faq.open ? '240px' : '0px'">
          <div class="al-faq-a-inner">{{faq.a}}</div>
        </div>
      </div>
    </div>
  </div>
</section>


<!-- ══════════════════════════════════════════════════════
     PASTE-IN DATA — add/replace these arrays in
     assessment-landing.component.ts
══════════════════════════════════════════════════════ -->
<!--

pillars = [
  {
    num: '01',
    cls: 'al-pillar-1',
    title: 'Academic Mastery',
    body: 'Every question is mapped to the National Curriculum, giving a clear, topic-by-topic view of where your child stands against age-related expectations.',
    tag: 'Curriculum Aligned'
  },
  {
    num: '02',
    cls: 'al-pillar-2',
    title: 'Higher-Order Thinking',
    body: 'We assess reasoning, analysis, creativity, and decision-making — the thinking skills that drive performance beyond the classroom and into unfamiliar problems.',
    tag: 'Thinking-Focused'
  },
  {
    num: '03',
    cls: 'al-pillar-3',
    title: 'Growth & Confidence',
    body: 'A personalised learning plan with SMART targets and home learning recommendations, built to build confidence and momentum, not just flag gaps.',
    tag: 'Growth-Focused'
  }
];

structureCards = [
  {
    num: '01',
    cls: 'al-sc-maths',
    title: 'Mathematics Thinking Challenge',
    mark: 'Curriculum + Reasoning',
    topics: [
      'Conceptual understanding & problem solving',
      'Logical reasoning & pattern recognition',
      'Accuracy & time management'
    ]
  },
  {
    num: '02',
    cls: 'al-sc-english',
    title: 'English Thinking Challenge',
    mark: 'Curriculum + Reasoning',
    topics: [
      'Critical thinking & analytical reading',
      'Communication skills',
      'Independent learning & creativity'
    ]
  }
];

howSteps = [
  { num: '1', cls: 'al-step-1', title: 'Register', body: "Choose your child's year group and preferred assessment session." },
  { num: '2', cls: 'al-step-2', title: 'Assessment', body: 'Students complete an engaging, age-appropriate Mathematics and English Thinking Challenge.' },
  { num: '3', cls: 'al-step-3', title: 'Expert Evaluation', body: "Our experienced educators carefully analyse every aspect of your child's performance." },
  { num: '4', cls: 'al-step-4', title: 'Comprehensive Performance Report', body: 'Receive a detailed digital report highlighting strengths, development areas, thinking skills, and personalised recommendations.' },
  { num: '5', cls: 'al-step-5', title: 'Parent Consultation', body: 'Meet with our educators to understand the report and discuss tailored strategies for improvement.' },
  { num: '6', cls: 'al-step-6', title: 'Continuous Growth', body: 'Follow personalised learning targets and monitor progress as your child develops confidence, resilience, and academic excellence.' }
];

consultationChecklist = [
  'Overall Academic Performance',
  'Strengths and Achievements',
  'Learning Gaps',
  'Critical Thinking Skills',
  'Confidence and Learning Behaviours',
  'Personalised Learning Strategies',
  'Home Support Techniques',
  'Future Academic Goals'
];

receivesCards = [
  { icon: '📄', title: 'Comprehensive Digital Performance Report', body: 'A full breakdown of achievement, curriculum topics, and academic competency ratings.' },
  { icon: '🧩', title: 'Topic-by-Topic Curriculum Analysis', body: 'Exactly where your child stands, topic by topic, against age-related expectations.' },
  { icon: '🧠', title: 'Higher-Order Thinking Skills Profile', body: 'A clear picture of reasoning, analysis, creativity, and decision-making ability.' },
  { icon: '🎯', title: 'Personalised Learning Plan', body: 'SMART improvement targets and home learning recommendations built around your child.' },
  { icon: '📈', title: 'Next-Year Readiness Evaluation', body: 'An honest read on how prepared your child is for the year ahead.' },
  { icon: '🤝', title: 'Individual Parent Consultation', body: 'A one-to-one session with an educator to turn the report into an action plan.' }
];

faqs = [
  { open: false, q: 'Which year groups can take part?', a: 'The Thinking Challenge Assessment is available for every year group from Year 3 through to Year 11.' },
  { open: false, q: 'What does the assessment cover?', a: "A Mathematics and English Thinking Challenge that combines National Curriculum content with higher-order thinking skills like reasoning, analysis, and problem solving." },
  { open: false, q: 'How is this different from a normal school test?', a: 'Most tests measure what your child knows. Ours also measures how they think — reasoning, creativity, decision-making, and independent learning — alongside curriculum performance.' },
  { open: false, q: "What happens after my child's assessment?", a: 'Within 48 hours you receive a full digital performance report, followed by a one-to-one consultation with an experienced educator to walk through the results and next steps.' },
  { open: false, q: 'Is the parent consultation really included for free?', a: 'Yes — every registered family receives the assessment, the digital report, and the one-to-one consultation at no cost.' }
];

-->